import HistoryIcon from "@mui/icons-material/History";
import { Box, Chip, Stack, Typography } from "@mui/material";
import InternCard from "../components/InternCard";
import InternStatusBadge from "../components/InternStatusBadge";
import InternTable, { InternTableColumn } from "../components/InternTable";

interface PendingChange {
  field: string;
  new_value?: string;
  changed_at: string;
  requires_build?: boolean;
}

interface ScheduledBuildRow {
  id: string;
  platform: string;
  pendingChanges: PendingChange[];
  firstChangeAt: string;
  lastChangeAt: string;
  scheduledFor: string;
  status: string;
  changesCount: number;
  pipelineId?: string | null;
}

interface Props {
  builds: ScheduledBuildRow[];
  /** Render without outer card chrome */
  embedded?: boolean;
  /** Scrollable table body height when content overflows */
  tableMaxHeight?: number | string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "waiting":
      return (
        <InternStatusBadge status="warning" label="Waiting" size="small" />
      );
    case "triggered":
      return (
        <InternStatusBadge status="success" label="Triggered" size="small" />
      );
    case "canceled":
      return (
        <InternStatusBadge status="default" label="Canceled" size="small" />
      );
    default:
      return <InternStatusBadge status="default" label={status} size="small" />;
  }
};

const formatFieldName = (field: string): string => {
  // Convert snake_case to Title Case
  return field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const columns: InternTableColumn<ScheduledBuildRow>[] = [
  {
    id: "platform",
    label: "Platform",
    width: 100,
    render: (row) => (
      <Chip
        label={row.platform.toUpperCase()}
        size="small"
        sx={{
          bgcolor:
            row.platform === "ios"
              ? "rgba(0, 122, 255, 0.1)"
              : "rgba(61, 220, 132, 0.1)",
          color: row.platform === "ios" ? "#007AFF" : "#3DDC84",
          fontWeight: 600,
          fontSize: "0.6875rem",
        }}
      />
    ),
  },
  {
    id: "status",
    label: "Status",
    width: 120,
    render: (row) => getStatusBadge(row.status),
  },
  {
    id: "changesCount",
    label: "Changes",
    width: 80,
    align: "center",
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: "var(--intern-text-primary)",
          fontWeight: 600,
          fontSize: "0.8125rem",
        }}
      >
        {row.changesCount}
      </Typography>
    ),
  },
  {
    id: "scheduledFor",
    label: "Scheduled",
    width: 150,
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: "var(--intern-text-secondary)",
          fontSize: "0.75rem",
        }}
      >
        {formatDate(row.scheduledFor)}
      </Typography>
    ),
  },
  {
    id: "lastChangeAt",
    label: "Last Change",
    width: 150,
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontSize: "0.75rem",
        }}
      >
        {formatDate(row.lastChangeAt)}
      </Typography>
    ),
  },
  {
    id: "pendingChanges",
    label: "Fields",
    render: (row) => {
      const fields = (row.pendingChanges || [])
        .map((c) => formatFieldName(c.field))
        .slice(0, 4);
      const extra = (row.pendingChanges?.length || 0) - fields.length;
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {fields.map((field) => (
            <Chip
              key={field}
              label={field}
              size="small"
              sx={{ fontSize: "0.625rem", height: 20 }}
            />
          ))}
          {extra > 0 ? (
            <Chip
              label={`+${extra}`}
              size="small"
              sx={{ fontSize: "0.625rem", height: 20 }}
            />
          ) : null}
        </Stack>
      );
    },
  },
];

const InternScheduledBuildsTable = ({
  builds,
  embedded = false,
  tableMaxHeight,
}: Props) => {
  const emptyState = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        height: tableMaxHeight ?? undefined,
        color: "var(--intern-text-tertiary)",
      }}
    >
      <HistoryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
      <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
        No scheduled build history
      </Typography>
    </Box>
  );

  if (builds.length === 0) {
    if (embedded) return emptyState;
    return (
      <InternCard title="Scheduled Builds History" variant="compact" fullHeight>
        {emptyState}
      </InternCard>
    );
  }

  const table = (
    <InternTable
      columns={columns}
      data={builds}
      keyExtractor={(row) => row.id}
      variant="compact"
      emptyMessage="No scheduled builds"
      stickyHeader={Boolean(tableMaxHeight)}
      maxHeight={tableMaxHeight}
      nested={embedded}
    />
  );

  if (embedded) return table;

  return (
    <InternCard
      title="Scheduled Builds History"
      variant="compact"
      noPadding
      fullHeight
    >
      {table}
    </InternCard>
  );
};

export default InternScheduledBuildsTable;
